'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { formatRelative } from '@/lib/format';
import { useSSE } from '@/hooks/useSSE';
import type { Notification } from '@/lib/photos/schemas';
import { Button } from '@/components/ui/Button';

/**
 * NotificationBell — Bell icon with dropdown notification panel.
 *
 * Renders in the GlobalHeader. Shows unread count badge.
 * SSE connection for real-time updates, initial load via REST.
 */
export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Re-fetch notifications + authoritative unread count from the server.
  const reload = useCallback(async () => {
    try {
      const res = await fetch('/api/notifications?limit=20&unread_only=false');
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  }, []);

  // Initial load
  useEffect(() => {
    void reload();
  }, [reload]);

  // SSE for real-time updates
  const handleNewNotification = useCallback((n: Notification) => {
    setNotifications((prev) => {
      const updated = [n, ...prev.filter((p) => p.id !== n.id)];
      return updated.slice(0, 50); // Keep max 50
    });
    setUnreadCount((prev) => prev + 1);
  }, []);

  useSSE<Notification>('/api/notifications/stream', 'notification', { onMessage: handleNewNotification });

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Mark all read — single bulk request, no N-fanout, no silent swallow.
  async function handleMarkAllRead() {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    // Optimistic update so the badge clears immediately.
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: 1, read_at: new Date().toISOString() }))
    );
    setUnreadCount(0);
    try {
      const res = await fetch('/api/field/notifications', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_all_read' }),
      });
      if (!res.ok) throw new Error(`mark_all_read failed: ${res.status}`);
    } catch (e) {
      // Re-fetch authoritative state from the server — the optimistic
      // update must be rolled back, and the true unread count may exceed
      // the 20 loaded notifications or have changed via SSE in flight.
      void reload();
      console.error('Failed to mark all notifications as read', e);
    }
  }

  // Mark single notification read and navigate
  async function handleNotificationClick(n: Notification) {
    if (!n.is_read) {
      try {
        await fetch(`/api/notifications/${n.id}/read`, { method: 'POST' });
        setNotifications((prev) =>
          prev.map((p) =>
            p.id === n.id ? { ...p, is_read: 1, read_at: new Date().toISOString() } : p
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (e) {
        console.error('Failed to mark notification as read', e);
      }
    }
    if (n.action_url) {
      router.push(n.action_url);
    }
    setIsOpen(false);
  }

  // Re-render every 60s so formatRelative() timestamps stay fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);


  // Priority border color
  function priorityBorderClass(priority: string): string {
    switch (priority) {
      case 'high':
      case 'urgent':
        return 'border-l-rose-400';
      case 'normal':
        return 'border-l-amber-400';
      default:
        return 'border-l-zinc-600';
    }
  }

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="text-zinc-400 hover:text-white p-1.5 rounded-md hover:bg-bg-overlay transition-colors relative"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold bg-brand-gold text-brand-charcoal rounded-full leading-none">
            {badgeText}
          </span>
        )}
      </Button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] sm:w-96 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto bg-bg-surface border border-border-subtle rounded-xl shadow-2xl z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle sticky top-0 bg-bg-surface z-10">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllRead}
                className="text-xs text-white hover:text-white/80 transition-colors"
              >
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-500">
              No notifications yet
            </div>
          ) : (
            <div>
              {notifications.map((n) => (
                <Button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-4 py-3 border-l-2 hover:bg-bg-overlay transition-colors flex items-start gap-3 ${priorityBorderClass(n.priority)} ${!n.is_read ? 'bg-bg-elevated/50' : ''}`}
                >
                  {/* Unread Indicator */}
                  <div className="mt-1.5 flex-shrink-0">
                    {!n.is_read ? (
                      <span className="block w-2 h-2 rounded-full bg-blue-500" />
                    ) : (
                      <span className="block w-2 h-2" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-200 truncate">
                      {n.title}
                    </div>
                    {n.body && (
                      <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">
                        {n.body}
                      </div>
                    )}
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {formatRelative(n.created_at)}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
